"use client";

import React, { useState, useRef } from 'react';
import { motion, useMotionValue, useTransform, animate, useAnimation } from 'framer-motion';
import { Loader2, ChevronDown } from '@/components/icons';
import { cn } from '@/lib/utils';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

const PullToRefresh: React.FC<PullToRefreshProps> = ({ onRefresh, children, className, disabled = false }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pullDistance = 55; // Slightly reduced for better feel
  
  const y = useMotionValue(0);
  const controls = useAnimation();
  const containerRef = useRef<HTMLDivElement>(null);
  const isMounted = useRef(true);
  const hasVibrated = useRef(false);

  React.useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // Haptic feedback function
  const triggerHaptic = (type: 'impact' | 'success' = 'impact') => {
    if (typeof window !== 'undefined' && window.navigator && window.navigator.vibrate) {
      if (type === 'success') {
        window.navigator.vibrate([10, 30, 10]);
      } else {
        window.navigator.vibrate(10);
      }
    }
  };

  // Improved indicator movement: stays visible more clearly at the bottom of the revealed area
  const indicatorOpacity = useTransform(y, [5, pullDistance * 0.6], [0, 1]);
  const indicatorScale = useTransform(y, [5, pullDistance * 0.6], [0.8, 1]);
  const iconRotate = useTransform(y, [0, pullDistance], [0, 180]);
  const textOpacity = useTransform(y, [pullDistance * 0.5, pullDistance], [0, 1]);

  const handleStart = (startY: number) => {
    if (isRefreshing || disabled) return;
    
    // Reset haptic state for new pull
    hasVibrated.current = false;
    
    // Attempt to find the correct scrollable parent
    const scrollParent = containerRef.current?.closest('.overflow-y-auto, .overflow-auto, main[role="main"]');
    const scrollTop = scrollParent ? scrollParent.scrollTop : window.scrollY;
    
    // Relaxed check: if scrollTop <= 5, we can pull
    if (scrollTop > 5) return;
    
    let isPulling = false;
    let isCleanedUp = false;
    
    const onMove = (currentY: number) => {
      const diff = currentY - startY;
      
      if (diff > 0) {
        // Less dramatic exponential resistance for snappier feel
        const newY = Math.min(Math.pow(diff, 0.85) * 1.2, pullDistance * 1.4);
        
        if (newY > 2) {
          isPulling = true;
          y.set(newY);

          // Vibration when threshold is reached
          if (newY >= pullDistance && !hasVibrated.current) {
            triggerHaptic('impact');
            hasVibrated.current = true;
          } else if (newY < pullDistance && hasVibrated.current) {
            // Reset if user pulls back up
            hasVibrated.current = false;
          }
          
          return true; // handled
        }
      }
      return false;
    };
    
    const cleanupListeners = () => {
      if (isCleanedUp) return; // Prevent double cleanup
      isCleanedUp = true;
      
      window.removeEventListener('touchmove', onTouchMove, { passive: false } as any);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    
    const onEnd = async () => {
      cleanupListeners();
      
      if (isPulling && y.get() >= pullDistance) {
        if (isMounted.current) setIsRefreshing(true);
        triggerHaptic('success');
        
        // Snap to refresh position
        await controls.start({ y: pullDistance, transition: { type: "spring", stiffness: 300, damping: 30 } });
        
        try {
          const startTime = Date.now();
          await onRefresh();
          const duration = Date.now() - startTime;
          if (duration < 1000) await new Promise(r => setTimeout(r, 1000 - duration));
        } catch (err) {
          console.error("Error during PullToRefresh:", err);
        } finally {
          if (isMounted.current) {
            setIsRefreshing(false);
            animate(y, 0, { type: "spring", stiffness: 300, damping: 30 });
          }
        }
      } else if (isPulling) {
        animate(y, 0, { type: "spring", stiffness: 300, damping: 30 });
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (onMove(e.touches[0].pageY)) {
        if (e.cancelable) e.preventDefault();
      }
    };

    const onMouseMove = (e: MouseEvent) => {
      onMove(e.pageY);
    };

    const onTouchEnd = () => onEnd();
    const onMouseUp = () => onEnd();
    
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    handleStart(e.touches[0].pageY);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    handleStart(e.pageY);
  };

  return (
    <div 
      ref={containerRef}
      className={cn("relative w-full overflow-hidden", className)}
      onTouchStart={handleTouchStart}
      onMouseDown={handleMouseDown}
    >
      {/* Content wrapper that moves DOWN during pull */}
      <motion.div 
        style={{ y }}
        animate={controls}
        className="relative z-10 w-full"
      >
        {/* Hidden area that exists ABOVE the content and is revealed as content slides down */}
        <div 
          className="absolute left-0 right-0 flex flex-col items-center justify-end pb-3 pointer-events-none overflow-visible"
          style={{ 
            top: -200, // Very high top to never cut off during spring back
            height: 200,
            backgroundColor: 'var(--primary)' // Green like the header
          }}
        >
          <motion.div 
            style={{ 
              opacity: indicatorOpacity,
              scale: indicatorScale
            }}
            className="flex flex-col items-center gap-1.5"
          >
            <div className="flex items-center justify-center rounded-full bg-card shadow-xl w-9 h-9 border border-border">
              {isRefreshing ? (
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
              ) : (
                <motion.div style={{ rotate: iconRotate }}>
                  <ChevronDown className="w-4 h-4 text-primary" />
                </motion.div>
              )}
            </div>
            
            <motion.span 
              style={{ opacity: isRefreshing ? 1 : textOpacity }}
              className="text-[9px] font-black text-primary uppercase tracking-wider bg-card px-2.5 py-1 rounded-full shadow-sm"
            >
              {isRefreshing ? 'Actualizando...' : 'Soltar para refrescar'}
            </motion.span>
          </motion.div>
        </div>

        {children}
      </motion.div>
    </div>
  );
};

export default PullToRefresh;
